import type {
  CurrencyAmount,
  IsoDate,
  IsoDateTime,
} from '@omnipresentgroup/design-system';
import type { Country } from 'libs/types/common';

import type {
  InvoiceReport,
  InvoiceReportEmployee,
  InvoiceReportEmployeeBillingBreakdown,
  InvoiceSyncStatusTarget,
  InvoiceSyncStatusType,
  SyncAbortedStatusReasonType,
} from '../invoiceReports';

export enum InvoiceReportStatusAPI {
  FULLY_APPLIED = 'FULLY_APPLIED',
  OPEN = 'OPEN',
  PAID_IN_FULL = 'PAID_IN_FULL',
  NOT_DEPOSITED = 'NOT_DEPOSITED',
  DEPOSITED = 'DEPOSITED',
  VOIDED = 'VOIDED',
}

export enum InvoiceReportNetSuiteTypeAPI {
  INVOICE = 'INVOICE',
  CREDIT = 'CREDIT',
  PAYMENT = 'PAYMENT',
  REFUND = 'REFUND',
}

export enum InvoiceReportTypeAPI {
  PAYROLL = 'PAYROLL',
  DEPOSIT = 'DEPOSIT',
  CASH_RECEIPTS = 'CASH_RECEIPTS',
  SETUP_FEE = 'SETUP_FEE',
  MISC = 'MISC',
  SALARY_PREPAYMENT = 'SALARY_PREPAYMENT',
  JOURNAL = 'JOURNAL',
  COST_RECHARGE_INVOICE = 'COST_RECHARGE_INVOICE',
  SALARY_BONUS_PREFUNDING = 'SALARY_BONUS_PREFUNDING',
  MANAGEMENT_FEE = 'MANAGEMENT_FEE',
  MANAGEMENT = 'MANAGEMENT',
  FEE = 'FEE',
  LATE_PAYMENT_INTEREST = 'LATE_PAYMENT_INTEREST',
  FX_FEES = 'FX_FEES',
  CUSTOMER_REFUND = 'CUSTOMER_REFUND',
}

export type SyncAbortedStatusReasonEntityAPI = {
  id: string;
  details?: {
    name: string;
    url?: string;
  };
};

type OrganisationMappingNotFoundReasonAPI = {
  type: SyncAbortedStatusReasonType.ORGANISATION_MAPPING_NOT_FOUND;
};

type ContactNotActiveReasonAPI = {
  type: SyncAbortedStatusReasonType.CONTACT_NOT_ACTIVE;
  xero_contact: SyncAbortedStatusReasonEntityAPI;
  xero_organisation: SyncAbortedStatusReasonEntityAPI;
};

type CategoryMappingsMissingReasonAPI = {
  type: SyncAbortedStatusReasonType.CATEGORY_MAPPINGS_MISSING;
  categories: SyncAbortedStatusReasonEntityAPI[];
  xero_organisation: SyncAbortedStatusReasonEntityAPI;
};

type TaxMismatchReason = {
  type: SyncAbortedStatusReasonType.TAX_MISMATCH;
};

type InvoiceNumberAlreadyExistsReasonAPI = {
  type: SyncAbortedStatusReasonType.INVOICE_NUMBER_ALREADY_EXISTS;
  xero_organisation: SyncAbortedStatusReasonEntityAPI;
};

type AccountNotValidForDocumentReasonAPI = {
  type: SyncAbortedStatusReasonType.ACCOUNT_NOT_VALID_FOR_DOCUMENT;
  xero_account: SyncAbortedStatusReasonEntityAPI & {
    details?: { code: string };
  };
  xero_organisation: SyncAbortedStatusReasonEntityAPI;
};

type AccountArchivedReasonAPI = {
  type: SyncAbortedStatusReasonType.ACCOUNT_ARCHIVED;
  xero_account: SyncAbortedStatusReasonEntityAPI & {
    details?: { code: string };
  };
  xero_organisation: SyncAbortedStatusReasonEntityAPI;
};

export type InvoiceSyncAbortedReasonAPI =
  | OrganisationMappingNotFoundReasonAPI
  | ContactNotActiveReasonAPI
  | CategoryMappingsMissingReasonAPI
  | InvoiceNumberAlreadyExistsReasonAPI
  | AccountNotValidForDocumentReasonAPI
  | AccountArchivedReasonAPI
  | TaxMismatchReason;

type InvoiceSyncStatusAPI = {
  status: InvoiceSyncStatusType;
  sync_attempted_at: IsoDateTime;
  sync_target: InvoiceSyncStatusTarget;
  xero_financial_document?: {
    id: string;
    details?: {
      url: string;
    };
  };
  reason?: InvoiceSyncAbortedReasonAPI;
};

export type InvoiceReportAPI = {
  id: string;
  reference: string;
  due_date: IsoDate;
  issue_date: IsoDate;
  company_id: string;
  total_amount: CurrencyAmount;
  total_amount_excluding_fees: CurrencyAmount;
  status: InvoiceReportStatusAPI;
  type: InvoiceReportNetSuiteTypeAPI;
  parent_transaction_id?: string;
  employee_count: number;
  invoice_type: InvoiceReportTypeAPI;
  approved: boolean | null;
  invoicing_emails: string[];
  sync_statuses?: InvoiceSyncStatusAPI[];
};

export type InvoiceReportEmployeeAPI = {
  id: string;
  full_name: string;
  email: string;
  salary?: CurrencyAmount;
  first_pay_date?: IsoDate;
  last_pay_date?: IsoDate;
  country: Country;
  role?: string;
  total_prev_invoice?: CurrencyAmount;
  billed_amount?: CurrencyAmount;
  billed_amount_excluding_fees?: CurrencyAmount;
  local_amount_excluding_fees?: CurrencyAmount;
  billing_notes?: string;
  prev_invoices?: Array<{
    pay_period: IsoDate;
    id: string;
  }>;
};

export type InvoiceReportEmployeeBreakdownItemAPI = {
  label: string;
  amount: number;
  items: InvoiceReportEmployeeBreakdownItemAPI[];
};

export type InvoiceReportEmployeeBillingBreakdownAPI = {
  id: string;
  pay_period: IsoDate;
  currency: string;
  data: {
    total_amount: number;
    items: InvoiceReportEmployeeBreakdownItemAPI[];
  };
  compared_invoice?: InvoiceReportEmployeeBillingBreakdownAPI;
};

export type GetInvoiceReportsResponseAPI = {
  count: number;
  data: InvoiceReportAPI[];
};

export type GetInvoiceReportResponseAPI = InvoiceReportAPI;

export type GetInvoiceReportEmployeesResponseAPI = {
  total_count: number;
  pages: number;
  size: number;
  data: InvoiceReportEmployeeAPI[];
};

export type GetInvoiceReportEmployeeResponseAPI = InvoiceReportEmployeeAPI;

export type GetInvoiceReportEmployeeBillingBreakdownResponseAPI =
  InvoiceReportEmployeeBillingBreakdownAPI;

/**
 * Model
 */

export type GetInvoiceReportsResponse = {
  count: number;
  data: InvoiceReport[];
};

export type GetInvoiceReportResponse = InvoiceReport;

export type GetInvoiceReportEmployeesResponse = {
  totalCount: number;
  pages: number;
  size: number;
  data: InvoiceReportEmployee[];
};

export type GetInvoiceReportEmployeeResponse = InvoiceReportEmployee;

export type GetInvoiceReportEmployeeBillingBreakdownResponse =
  InvoiceReportEmployeeBillingBreakdown;
